<script setup>
import { computed, ref, nextTick } from "vue";
import axios from "axios";
import i18n from "@/i18n";
import { useVideoCallState } from "@/stores/videoCallState";
import { EventManager, Actions, Events } from "@events";
import { toastError } from "@/utils";

const props = defineProps({
  rtcId: {
    type: String,
    required: true,
  },
  sender: {
    type: String,
    //required: true,
  },
  senderName: {
    type: String,
    //required: true,
  },
});

const loading = ref(false);
const state = useVideoCallState(props.rtcId);
const FILE_SHARE_CONFIG = window.CONFIG.CHAT.FILE_SHARE;
const acceptedFileTypes = FILE_SHARE_CONFIG.ACCEPTED_FILE_TYPES;
const fileSharedEnabled = ref(false);
const FILE_SHARE_ENABLED = window.CONFIG.CHAT.FILE_SHARE.ENABLED;

fileSharedEnabled.value =
  (state?.role == "agent" && FILE_SHARE_ENABLED.AGENT) ||
  (state?.role == "user" && FILE_SHARE_ENABLED.USER);

const actionEmitter = EventManager.getActionEmitter(props.rtcId);
const eventEmitter = EventManager.getEventEmitter(props.rtcId);

const files = ref([]);

async function selectFile() {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = acceptedFileTypes.join(", ");

  input.multiple = true;

  input.onchange = (e) => {
    // force convert to array
    files.value = Array.from(e.target.files);
  };
  input.click();
}

function removeFile(index) {
  files.value.splice(index, 1);
}

async function uploadFiles() {
  let copy = [...files.value];
  for (let file of copy) {
    try {
      const form = new FormData();
      form.append("Files", file);
      form.append("RtcId", props.rtcId);
      form.append("Sender", props.sender);
      form.append("SenderName", props.senderName);

      const response = await axios.post("/api/fileshare/upload", form, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.data?.success) {
        const data = response.data.data;
        const url = `${data.id}/${data.name}`;
        await sendMessage("file", url, data.createdAt);
        eventEmitter.emit(Events.FileShared, response.data);
        // remove file from list
        removeFile(files.value.indexOf(file));
      } else {
        console.error(response.data?.data);
      }
    } catch (error) {
      console.error(error);
      if (error.response) {
        console.error(error.response?.data);
        console.error(error.response?.data?.error);
        console.error(error.response?.data?.errorCode);
        toastError(error.response?.data);
      }
    }
  }
}

function getSenderType() {
  const isAgent = state.role == "agent";
  return isAgent ? "agent" : "user";
}

async function sendMessage(type, data, createdAt = new Date()) {
  const message = {
    type: "text-chat",
    data: {
      sender: {
        type: getSenderType(),
        name: state?.name, // peerName boş ise type ile gösterim.
      },
      messageType: type, // text | file | location
      message: data,
      createdAt: createdAt,
    },
  };
  actionEmitter.emit(Actions.SendSignalingMessage, message);
}

const messageForm = ref("");
const messageFormRef = ref(null);

async function send() {
  loading.value = true;

  if (messageForm.value.trim()) {
    await sendMessage("text", messageForm.value);
    messageForm.value = "";
  }

  if (files.value.length > 0) {
    await uploadFiles();
  }

  loading.value = false;

  // Focus on the input after sending the message
  nextTick(() => {
    messageFormRef.value.focus();
  });
}

const sendIcon = computed(() => {
  if (loading.value) {
    return "mdi-loading";
  }

  if (messageForm.value.trim() || files.value.length > 0) {
    return "mdi-send";
  }

  return "";
});
</script>
<template>
  <v-text-field
    ref="messageFormRef"
    :placeholder="$t('TypeMessage')"
    :disabled="loading"
    v-model="messageForm"
    label=""
    type="text"
    no-details
    outlined
    :append-inner-icon="sendIcon"
    @keyup.enter="() => send()"
    @click:append-inner="() => send()"
    hide-details
  >
    <div style="display: flex" v-if="fileSharedEnabled">
      <div>
        <v-btn density="comfortable" icon @click="selectFile" variant="plain">
          <v-icon>mdi-paperclip</v-icon>
        </v-btn>
      </div>
      <div>
        <v-chip
          density="comfortable"
          size="small"
          v-for="(file, index) in files"
          :key="index"
          @click="removeFile(index)"
        >
          <span
            style="
              max-width: 80px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            "
          >
            {{ file.name }}
          </span>
          <v-icon color="error"> mdi-close </v-icon>
        </v-chip>
      </div>
    </div>
  </v-text-field>
</template>
